<template>
  <div>
    <BulkOpsHeader
      @new="newDialog = true"
    >
      Contact Bulk Import
    </BulkOpsHeader>
    <BulkOpsTable
      :rows.sync="formOps"
      :headers="headers"
      :totalRows="pagination.totalRows"
      :pagination="pagination"
      @fetch="pagination=arguments[0]"
    >
      <template v-slot:default="prop">
        <td>
          <span
            v-if="prop.row.status == 'running'"
          >
            <b>Running...</b>
          </span>
          <span
            v-else
          >
            <b>Imported {{prop.row.count}} Contacts</b>
          </span>
          <span
            v-if="prop.row.failed > 0"
            style="color: red;"
          >
            ({{prop.row.failed}} failed!)
          </span>
          <div
            v-if="JSON.parse(prop.row.import_json).tags"
          >
            <b>Add Tags: </b>
            <v-chip
              v-for="tag in JSON.parse(prop.row.import_json).tags"
              :key="tag"
              small
            >
              {{tag}}
            </v-chip>
          </div>
          <div
            v-if="JSON.parse(prop.row.import_json).active"
          >
            <b>Enabled</b>
          </div>
        </td>
        <td style="text-transform: capitalize;">
          <span
            v-if="prop.row.failed"
          >
            <v-btn
              flat
              color="red"
              small
              @click="getErrorFile(prop.row.id)"
            >
              Download Errors
            </v-btn>
          </span>
        </td>
        <td style="text-transform: capitalize;">
          {{prop.row.status}}
        </td>
      </template>
    </BulkOpsTable>
    <BulkOpsDialog
      v-if="newDialog"
      title="Import Clients"
      :update_actions="bulkOpActions"
      :disabled="cantSubmit"
      @save="save(arguments[0])"
      @cancel="newDialog = false"
    >
      <v-card-text>
        <div style="display:inline-block;width:100%;height:32px;color:lightgrey;font-size:18px;">
          <input
            type="file"
            ref="files"
            accept=".csv"
            v-on:change="handleFileUploads()"
          />
          <v-btn
            flat
            small
            color="primary"
            href="/assets/contact_import.csv"
          >
            Download Template CSV
          </v-btn><br />
          <div
            style="color: black;font-size:14px;"
          >
            <b>Importing accepts the following column headers (bold, required): </b><br />
            <b>cuid, email, first name, middle name, last name</b>, phone, notify email, notify sms, tags<br />
            <i>cuid and email must be unique</i> - duplicate or already existing contacts will cause the import to fail.<br /><br />
            <b>The following headers are recommended if adding a case:</b>
            <br />
            <b>case number</b> - A number to identify the case<br />
            <b>case title</b> - The name of the case<br /><br />
            <b>The following headers are recommended if adding a form:</b>
            <br />
            <b>form number</b> - A number to identify the form, usually the contact's cuid<br />
            <b>party name</b> - The party completing the form, usually the contact's name<br /><br />
            To set notification preferences, set the "notify email/sms" value in the spreadsheet to 1 to enable, and 0 to disable.
          </div>
        </div>
      </v-card-text>
    </BulkOpsDialog>
  </div>
</template>

<script>
import api from '@/api'
import moment from 'moment'
import GlobalConstants from '@/globals'
import pagination from '@/components/global/pagination'
import BulkOpsHeader from '@/components/admin/bulkops/BulkOpsHeader'
import BulkOpsTable from '@/components/admin/bulkops/BulkOpsTable'
import BulkOpsDialog from '@/components/admin/bulkops/BulkOpsDialog'

export default {
  name: 'ContactImport',
  components: {
    BulkOpsHeader,
    BulkOpsTable,
    BulkOpsDialog
  },
  mixins: [
    pagination
  ],
  data () {
    return {
      formOps: [],
      files: [],
      loading: false,
      pagination: {},
      totalRows: null,
      pages: null,
      newDialog: false,
      interval: null,
      headers: [
        { text: 'Details', width: 180, sortable: false },
        { text: 'Files', width: 60, sortable: false },
        { text: 'Status', width: 60, sortable: false },
        { text: 'Run Time', width: 60, sortable: false },
        { text: 'Progress', width: 60, sortable: false },
      ],
      bulkOpActions: [
        {
          "title": "Add Tags",
          "prop": "tags",
          "type": "tags",
          "type_scope": "contact",
          "value": null,
          "hint": "Automatically Tag Created Contacts",
          "required": true
        },
        {
          "title": "Set Active Status",
          "prop": "active",
          "type": "dropdown",
          "options": [
            {
              "name": "Enabled",
              "value": true
            },
            {
              "name": "Disabled",
              "value": false
            }
          ],
          "value": true,
          "hint": "Set Contacts Enabled or Disabled on Import"
        },
        {
          "title": "Add Case",
          "prop": "add_case",
          "type": "checkbox",
          "value": null,
          "hint": "Add Case to Contact on creation"
        },
        {
          "title": "Case Type",
          "prop": "case_type",
          "type": "selection",
          "type_scope": "casetypes",
          "value": null,
          "hint": "Case Type to Add to Contact",
          "required": true,
          "prereq": "add_case",
        },
        {
          "title": "Add Form",
          "prop": "add_form",
          "type": "checkbox",
          "value": null,
          "hint": "Add Form to Contact on creation",
          "prereq": "add_case",
        },
        {
          "title": "Template",
          "prop": "form_template",
          "type": "selection",
          "type_scope": "templates",
          "value": null,
          "hint": "Template to Assign to Contacts",
          "required": true,
          "prereq": "add_form"
        },
        {
          "title": "Add Tags",
          "prop": "form_tags",
          "type": "tags",
          "type_scope": "userform",
          "value": null,
          "hint": "Automatically Tag Created Forms",
          "required": true,
          "prereq": "add_form"
        },
        {
          "title": "Set Status",
          "prop": "form_status",
          "type": "dropdown",
          "options": GlobalConstants.StatusStrings,
          "value": "new",
          "hint": "Set all forms to this status",
          "prereq": "add_form"
        },
      ]
    }
  },
  computed: {
    cantSubmit () {
      return this.files.length == 0
    }
  },
  created () {
    this.documentTitle('Contact Import')
  },
  methods: {
    handleFileUploads () {
      this.files = this.$refs.files.files
      if (this.files.length > 0) {
        this.canRun = true
      }
    },
    restartInterval () {
      clearInterval(this.interval)
      var countWorking = 0
      this.formOps.forEach((item) => {
        if (["finished", "failed"].indexOf(item.status) == -1)
          countWorking++
      })
      if (countWorking > 0) {
        this.interval = setInterval(() => {
          this.fetch()
        }, 5000)
      }
    },
    fetch () {
      this.loading = true
      api.bulkops.contact_import.list({
        pageNum: this.pagination.page, count: this.pagination.rowsPerPage, ordering: '-created_at'
      })
        .then(data => {
          this.formOps = data.results
          this.pagination.totalRows = data.count
          this.pagination.page = data.page_number
          this.loading = false
          this.componentCreated = true
          this.restartInterval()
        })
    },
    save (newOp) {
      let formData = new FormData()
      for( var i = 0; i < this.files.length; i++ ){
        let file = this.files[i]
        formData.append(`import_csv`, file)
      }
      var import_json = {}
      newOp.props.forEach((prop) => {
        if (prop.prop == 'form_template' && prop.value) {
          import_json['form_template'] = prop.value.value
        } else if (prop.prop == 'case_type' && prop.value) {
          import_json['case_type'] = prop.value.value;
        } else {
          if (prop.value !== null && prop.value !== "") {
            import_json[prop.prop] = prop.value
          }
        }
      })
      formData.append(`import_json`, JSON.stringify(import_json))
      api.bulkops.contact_import.save(formData)
      .then(() => {
        this.newDialog = false
        this.fetch()
      })
    },
    getErrorFile (obj) {
      this.loading = true
      api.bulkops.contact_import.downloadFile(obj)
      .then(response => {
        this.loading = false
        let blob = new Blob([response.data])
        let link = document.createElement('a')
        link.href = window.URL.createObjectURL(blob)
        link.download = "Contact_Import-" + obj + '.csv'
        link.click()
      })
    },
  },
  beforeDestroy () {
    clearInterval(this.interval)
  }
}
</script>
